import { ActionIcon, Popover } from '@mantine/core';
import { IconEdit, IconFileText, IconHash } from '@tabler/icons-react';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChatOverview } from '../../api/chat';
import { Avatar } from '../../components/Avatar';
import { recentConversations, whenLabel } from '../../features/chat/chatHelpers';
import { useSession } from '../session';
import { useIsMobile } from '../useIsMobile';
import { RegText } from '../../components/plate';
import classes from './shell.module.css';

interface ChatPopoverProps {
  /** Renders the trigger; the header uses an icon button with the unread total. */
  target: (props: { opened: boolean; toggle: () => void; unread: number }) => ReactNode;
  position: 'bottom-end' | 'top';
}

/**
 * The header's chat icon: unread total (pushed over the realtime socket) and the most recent
 * conversations, unread first. Tapping one opens it on the Chat page.
 */
export function ChatPopover({ target, position }: ChatPopoverProps) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { signedIn } = useSession();
  const [opened, setOpened] = useState(false);
  const { data } = useChatOverview(signedIn);
  const unread = data?.totalUnread ?? 0;
  const recent = recentConversations(data);

  const open = (path: string) => {
    setOpened(false);
    navigate(path);
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      width={isMobile ? 'calc(100vw - 16px)' : 360}
      position={position}
      offset={isMobile ? 8 : 4}
      shadow="md"
      trapFocus
      withinPortal
    >
      <Popover.Target>
        <span style={{ display: 'contents' }}>
          {target({ opened, toggle: () => setOpened((o) => !o), unread })}
        </span>
      </Popover.Target>
      <Popover.Dropdown p={0} aria-label="Chat">
        <div className={classes.popHeader}>
          <button
            type="button"
            className={classes.popTitle}
            style={{
              background: 'none',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              padding: 0,
            }}
            onClick={() => open('/chat')}
          >
            Chat
          </button>
          {data?.canUse && (
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label="New message"
              size={32}
              onClick={() => open('/chat?new=1')}
            >
              <IconEdit size={18} stroke={2} />
            </ActionIcon>
          )}
        </div>
        <div className={classes.notifList}>
          {recent.length === 0 && (
            <div className={classes.notifEmpty}>No messages yet. Your branch team is in Chat.</div>
          )}
          {recent.map((t) => (
            <button
              key={t.id}
              type="button"
              className={classes.chatRow}
              onClick={() => open(`/chat?c=${t.id}`)}
            >
              {t.dmPerson ? (
                <Avatar initials={t.dmPerson.initials} size={36} />
              ) : (
                <span
                  aria-hidden="true"
                  style={{ width: 36, height: 36, display: 'grid', placeItems: 'center' }}
                >
                  {t.kind === 'record' ? <IconFileText size={20} /> : <IconHash size={20} />}
                </span>
              )}
              <span className={classes.chatText}>
                <span className={classes.chatTop}>
                  <span className={classes.chatName}>
                    {t.kind === 'record' ? <RegText text={t.name} /> : t.name}
                  </span>
                  <span className={classes.chatWhen}>{whenLabel(t.lastMessageAt)}</span>
                </span>
                <span className={classes.chatLast} data-unread={t.unread > 0 || undefined}>
                  {t.unread > 0 ? `${t.unread} unread · ` : ''}
                  {t.lastMessage
                    ? `${t.lastMessage.authorName && t.kind !== 'dm' ? `${t.lastMessage.authorName.split(' ')[0]}: ` : ''}${t.lastMessage.preview}`
                    : ''}
                </span>
              </span>
            </button>
          ))}
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}
